/**
 * QUIÉN VA ASEGURADO EN ESTA PÓLIZA.
 *
 * Lissette, 2026-09-19: *«cuando se le da impresión en el cliente, la ficha no
 * muestra las pólizas canceladas y las pólizas no muestran tampoco los miembros
 * en esas pólizas»*.
 *
 * La ficha impresa listaba a los familiares del cliente por un lado y las
 * pólizas por otro, y **nunca decía qué persona iba en qué póliza** — que es
 * justo lo que se mira cuando se imprime para una aseguradora o para que el
 * cliente lo revise.
 *
 * ── POR QUÉ ESTO ES UN MÓDULO Y NO DIEZ LÍNEAS EN LA IMPRESIÓN ─────────────
 *
 * La regla existía, y estaba bien, pero vivía DENTRO de `PolicyCard` en
 * `PoliciesView.jsx` — con un comentario que decía, literalmente, *«single
 * source of truth for the per-policy people math»*. No lo era: era la única
 * copia, que es distinto. Escribir una segunda en la impresión es exactamente
 * la forma del E-138, y aquí tendría una consecuencia fea y silenciosa: la
 * tarjeta diría «3 asegurados» y el papel listaría 2, sin que nada fallara.
 *
 * Así que la regla se saca aquí, la usan los dos, y se puede probar sin React.
 *
 * ── LA REGLA, Y LO QUE NO HACE ─────────────────────────────────────────────
 *
 * Asegurado es **PURAMENTE lo que marcó la agente** en `policyInsured`. Nadie
 * se añade solo: ni el titular, ni el cónyuge, ni los dependientes. Esa
 * decisión ya estaba tomada y no se toca — el día que se auto-añadía al
 * titular, la insignia decía 0 y las filas decían 1 (y la agente no sabía cuál
 * creer).
 *
 * Y el cónyuge **no se esconde por el estado civil**: una ficha que dice
 * «soltero» con un cónyuge nombrado y marcado como asegurado es una
 * incoherencia de los DATOS, y taparla en pantalla la vuelve invisible en vez
 * de arreglarla (Lissette, 2026-09-08).
 *
 * Sin dependencias: lo importan dos componentes y las pruebas.
 */

/**
 * Las personas aseguradas en esta póliza, en el orden en que se leen:
 * titular, cónyuge, dependientes, y al final los escritos a mano.
 *
 * Devuelve `[{ name, relation }]`. `relation` puede venir vacía —un dependiente
 * sin parentesco escrito, o un nombre suelto— y quien lo enseñe decide qué
 * poner en su lugar.
 *
 * `etiquetas` trae las dos palabras que dependen del idioma; se pasan de fuera
 * para que este archivo no tenga que saber de idiomas.
 */
export function aseguradosDeLaPoliza(policy, client, dependents, etiquetas = {}) {
  const titular = etiquetas.titular || 'Primary';
  const conyuge = etiquetas.conyuge || 'Spouse';
  const marcadas = Array.isArray(policy && policy.policyInsured) ? policy.policyInsured : [];
  const gente = [];

  if (client) {
    if (marcadas.includes('primary')) {
      gente.push({
        name: `${client.firstName || ''} ${client.lastName || ''}`.trim(),
        relation: titular,
      });
    }
    if (client.spouse && client.spouse.firstName && marcadas.includes('spouse')) {
      gente.push({
        name: `${client.spouse.firstName} ${client.spouse.lastName || ''}`.trim(),
        relation: conyuge,
      });
    }
  }

  // La clave de un dependiente es su `id`, y `dep<indice>` cuando no tiene —
  // las fichas viejas se guardaron así y siguen marcadas de esa forma.
  (dependents || []).forEach((d, i) => {
    if (!marcadas.includes(d.id || `dep${i}`)) return;
    gente.push({
      name: `${d.firstName || ''} ${d.lastName || ''}`.trim(),
      relation: d.relation || '',
    });
  });

  // Los escritos a mano en la póliza: alguien que no está en la ficha. Se
  // descartan los que REPITEN un nombre ya listado (sin distinguir mayúsculas):
  // pasa cuando se escribe a mano a alguien que después se añade a la familia,
  // y contarlo dos veces inflaría el número de asegurados.
  const yaEstan = new Set(gente.map((p) => p.name.toLowerCase()));
  ((policy && policy.policyInsuredExtra) || []).forEach((n) => {
    const nombre = String(n || '').trim();
    if (!nombre || yaEstan.has(nombre.toLowerCase())) return;
    yaEstan.add(nombre.toLowerCase());
    gente.push({ name: nombre, relation: '' });
  });

  return gente;
}

/**
 * Cuántas personas van aseguradas. Sale de la MISMA lista que se enseña, para
 * que el número y las filas no puedan discrepar — que es el fallo que ya se
 * arregló una vez dentro de la tarjeta.
 */
export function cuantosAsegurados(policy, client, dependents) {
  return aseguradosDeLaPoliza(policy, client, dependents).length;
}

// Estados en los que la póliza ya no cubre a nadie. Lo demás (activa,
// pendiente, periodo de gracia, un estado propio de la agencia) cuenta.
const SIN_COBERTURA = new Set(['CANCELLED', 'EXPIRED', 'LAPSED']);

/**
 * Cuántas PERSONAS DISTINTAS del cliente van aseguradas en sus pólizas que
 * siguen cubriendo. Es la columna "Asegurados" del Excel de clientes.
 *
 * Lissette, 2026-10-02: una agente vio una familia de 7 asegurados que en el
 * Excel salía con 6. El Excel contaba los interruptores de cobertura de la
 * ficha (`countInsuredMembers`), no lo marcado en la póliza: un nombre escrito
 * a mano, un interruptor sin encender o un cónyuge con estado civil "soltero"
 * contaban en la tarjeta y no en el papel (E-342).
 *
 * Por persona y no por póliza: quien va en el ACA y en el dental es UNA
 * persona asegurada, no dos. Se cuenta con `aseguradosDeLaPoliza`, la misma
 * regla de la tarjeta, así que no puede volver a separarse de ella.
 */
export function aseguradosDelCliente(policies, client, dependents) {
  const gente = [];
  (policies || []).forEach((p) => {
    if (!p || SIN_COBERTURA.has(String(p.status || '').toUpperCase())) return;
    gente.push(...aseguradosDeLaPoliza(p, client, dependents));
  });
  // Primero los de la ficha, luego los sueltos: un nombre escrito a mano en
  // una póliza puede ser alguien que en otra va marcado desde la ficha, y si
  // ese nombre ya salió es la misma persona, sea cual sea el orden de pólizas.
  const vistos = new Set();
  const nombres = new Set();
  gente.filter((x) => x.relation).forEach((x) => {
    nombres.add(x.name.toLowerCase());
    vistos.add(`${x.relation}|${x.name}`.toLowerCase());
  });
  gente.filter((x) => !x.relation).forEach((x) => {
    const nombre = x.name.toLowerCase();
    if (nombres.has(nombre)) return;
    nombres.add(nombre);
    vistos.add(`|${nombre}`);
  });
  return vistos.size;
}

/**
 * ¿La póliza ya empezó? (Ana María, Calle Seguros, 2026-11-04: *"when renewing
 * the policy, it is doubling the number of members"*; Lissette: *"contar los
 * activos actuales, no los de las pólizas que aún no son efectivas"*.)
 *
 * En plena renovación la póliza nueva (efectiva el 1 de enero) ya está viva
 * junto a la de este año, y la caja "Miembros por tipo de seguro" de la ficha
 * contaba a la familia dos veces. Una póliza sin fecha efectiva cuenta, como
 * antes. Las fechas son "YYYY-MM-DD" y se comparan con el día LOCAL.
 */
export function polizaYaEnVigor(p, hoy = new Date()) {
  const f = String(p?.effectiveDate || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f)) return true;
  const dos = (n) => String(n).padStart(2, '0');
  const hoyLocal = `${hoy.getFullYear()}-${dos(hoy.getMonth() + 1)}-${dos(hoy.getDate())}`;
  return f <= hoyLocal;
}
